/**
 * usePrestige - Main hook for Prestige 3D configurator
 * Integrates prestigeService with React state management
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  createPrestigeService,
  type PrestigeService,
  type FaceIDType,
  type SceneId,
  type AccessoryCapabilities,
  FaceID,
  PRESTIGE_DIMENSIONS,
  SCENES,
} from '@infrastructure/three/prestigeService';

export type UnitType = 'cm' | 'inches';
type AddPartOptions = {
  openCustomization?: boolean;
};

export type PrestigeState = {
  isLoading: boolean;
  loadProgress: number;
  error: string | null;
  widthValue: number;
  depthValue: number;
  heightValue: number;
  blindRotation: number;
  roomCount: number;
  hasWall: boolean;
  unit: UnitType;
  currentScene: SceneId;
  ledsVisible: boolean;
  heaterQuantity: number;
  nightMode: boolean;
  clickedFaceId: FaceIDType | null;
  activeAccessoryType: number | null;
  isAddMode: boolean;
  // Post-add customization modal
  customizingFaceId: FaceIDType | null;
  customizingPartType: number | null;
  customizingCapabilities: AccessoryCapabilities | null;
};

export type PrestigeActions = {
  setWidth: (value: number) => void;
  setDepth: (value: number) => void;
  setHeight: (value: number) => void;
  setBlindRotation: (degrees: number) => void;
  applyFrameColor: (textureImg: string) => void;
  applyBlindColor: (textureImg: string) => void;
  setWallMounted: (value: boolean) => void;
  setUnit: (unit: UnitType) => void;
  setScene: (sceneId: SceneId) => Promise<void>;
  setSceneVisibility: (visible: boolean) => void;
  toggleLedVisibility: () => void;
  setHeaterQuantity: (qty: number) => void;
  toggleNightMode: () => void;
  canAddPart: (faceId: string) => boolean;
  addPart: (faceId: string, partType: number, options?: AddPartOptions) => Promise<void>;
  deletePart: (faceId: string) => void;
  deleteAllParts: () => void;
  clearClickedFace: () => void;
  // Accessory customization
  setAccessoryMovement: (value: number) => void;
  setAccessoryMovementSecondary: (value: number) => void;
  setAccessoryLeftRight: (isLeft: boolean) => void;
  openCustomization: (faceId: FaceIDType, partType: number) => void;
  closeCustomizationModal: () => void;
  startAddMode: (partType: number) => void;
  stopAddMode: () => void;
  // Merge panel controls
  toggleMergeDoor: (doorIndex: number, visible: boolean) => void;
  getMergeDoors: () => boolean[];
  getMergeDoorCount: () => number;
  getAccessoryMovement: () => number;
  getAccessorySecondaryMovement: () => number;
  getAccessoryLeftRight: () => boolean;
  getAccessoryTexture: () => string;
  getAccessoryTextureSecondary: () => string;
  // Accessory textures
  setAccessoryTexture: (textureImg: string) => void;
  setAccessoryTextureSecondary: (textureImg: string) => void;
  reset: () => void;
};

export type UsePrestigeReturn = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  state: PrestigeState;
  actions: PrestigeActions;
  service: PrestigeService | null;
  view: {
    getCamera: () => THREE.PerspectiveCamera | null;
    getScene: () => THREE.Scene | null;
    captureImage: (mimeType?: string, quality?: number) => string | null;
  };
};

const IMAGE_BASE_PATH = `${import.meta.env.BASE_URL}images/`;
const DEFAULT_SCENE_BG = 0xf5f5f5;
const DIM_SCENE_BG = 0x252a36;
/**
 * When false, the sky.jpg skybox stays hidden and the configurator shows the
 * flat scene background (DEFAULT_SCENE_BG). Flip to true to restore the sky.
 */
const SHOW_SKYBOX = false;
const UNDERSIDE_VIEW_MAX_POLAR_ANGLE = 165 * (Math.PI / 180);
const UNDERSIDE_VIEW_MIN_POLAR_ANGLE = 5 * (Math.PI / 180);
const UNDERSIDE_FLOOR_HIDE_OFFSET = 0.05;
const DAY_LED_EMISSIVE_INTENSITY = 1.15;
const NIGHT_LED_EMISSIVE_INTENSITY = 3.2;
const DEFAULT_LIGHT_LEVELS = {
  hemisphere: 2,
  ambient: 0.35,
  key: 1,
  fill: 0.5,
  front: 0.55,
  back: 0.95,
} as const;
const DIM_LIGHT_LEVELS = {
  hemisphere: 0.6,
  ambient: 0.12,
  key: 0.42,
  fill: 0.18,
  front: 0.2,
  back: 0.3,
} as const;

export const usePrestige = (): UsePrestigeReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const serviceRef = useRef<PrestigeService | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const skyboxRef = useRef<THREE.Mesh | null>(null);
  const groundRef = useRef<THREE.Mesh | null>(null);
  const baseRef = useRef<THREE.Mesh | null>(null);
  const floorHiddenForUndersideViewRef = useRef(false);
  const defaultFloorVisibilityRef = useRef({ ground: true, base: true });
  const hemisphereLightRef = useRef<THREE.HemisphereLight | null>(null);
  const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
  const keyLightRef = useRef<THREE.DirectionalLight | null>(null);
  const fillLightRef = useRef<THREE.DirectionalLight | null>(null);
  const frontLightRef = useRef<THREE.DirectionalLight | null>(null);
  const backLightRef = useRef<THREE.DirectionalLight | null>(null);

  const [state, setState] = useState<PrestigeState>({
    isLoading: true,
    loadProgress: 0,
    error: null,
    widthValue: 19,
    depthValue: PRESTIGE_DIMENSIONS.depth[0] ?? 3,
    heightValue: PRESTIGE_DIMENSIONS.height[0] ?? 2.6,
    blindRotation: 0,
    roomCount: 1,
    hasWall: false,
    unit: 'inches',
    currentScene: null,
    ledsVisible: false,
    heaterQuantity: 0,
    nightMode: false,
    clickedFaceId: null,
    activeAccessoryType: null,
    isAddMode: false,
    customizingFaceId: null,
    customizingPartType: null,
    customizingCapabilities: null,
  });

  const raycasterRef = useRef<THREE.Raycaster | null>(null);
  const addPartRef = useRef<((faceId: string, partType: number, options?: AddPartOptions) => Promise<void>) | null>(null);
  const stopAddModeRef = useRef<(() => void) | null>(null);
  const deletePartRef = useRef<((faceId: string) => void) | null>(null);
  const openCustomizationRef = useRef<((faceId: FaceIDType, partType: number) => void) | null>(null);
  const addModeRef = useRef(false);
  const activeAccessoryTypeRef = useRef<number | null>(null);
  const nightModeRef = useRef(false);

  const setSceneShadowsEnabled = useCallback((enabled: boolean) => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;

    renderer.shadowMap.enabled = enabled;

    if (keyLightRef.current) {
      keyLightRef.current.castShadow = enabled;
      keyLightRef.current.shadow.autoUpdate = enabled;
      keyLightRef.current.shadow.needsUpdate = enabled;
    }

    if (groundRef.current) {
      groundRef.current.receiveShadow = enabled;
    }

    if (baseRef.current) {
      baseRef.current.receiveShadow = enabled;
    }

    scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        material.needsUpdate = true;
      });
    });
  }, []);

  const syncDefaultFloorVisibilityForCamera = useCallback((payload: {
    camera: THREE.PerspectiveCamera;
  }) => {
    const ground = groundRef.current;
    if (!ground) return;

    const base = baseRef.current;
    const shouldHideFloor = payload.camera.position.y < ground.position.y + UNDERSIDE_FLOOR_HIDE_OFFSET;
    if (shouldHideFloor === floorHiddenForUndersideViewRef.current) return;

    if (shouldHideFloor) {
      defaultFloorVisibilityRef.current = {
        ground: ground.visible,
        base: base?.visible ?? true,
      };
      ground.visible = false;
      if (base) base.visible = false;
      floorHiddenForUndersideViewRef.current = true;
      return;
    }

    ground.visible = defaultFloorVisibilityRef.current.ground;
    if (base) base.visible = defaultFloorVisibilityRef.current.base;
    floorHiddenForUndersideViewRef.current = false;
  }, []);

  const applyEnvironmentDimming = useCallback((dimmed: boolean) => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;

    const levels = dimmed ? DIM_LIGHT_LEVELS : DEFAULT_LIGHT_LEVELS;
    scene.background = new THREE.Color(dimmed ? DIM_SCENE_BG : DEFAULT_SCENE_BG);
    renderer.setClearColor(dimmed ? DIM_SCENE_BG : DEFAULT_SCENE_BG);

    if (hemisphereLightRef.current) hemisphereLightRef.current.intensity = levels.hemisphere;
    if (ambientLightRef.current) ambientLightRef.current.intensity = levels.ambient;
    if (keyLightRef.current) keyLightRef.current.intensity = levels.key;
    if (fillLightRef.current) fillLightRef.current.intensity = levels.fill;
    if (frontLightRef.current) frontLightRef.current.intensity = levels.front;
    if (backLightRef.current) backLightRef.current.intensity = levels.back;

    // Skybox intentionally kept hidden so the flat scene background shows.
    if (skyboxRef.current) {
      skyboxRef.current.visible = false;
    }

    if (groundRef.current) {
      const material = groundRef.current.material;
      if (material instanceof THREE.MeshStandardMaterial) {
        material.color.setHex(dimmed ? 0x2d3240 : 0xcccccc);
      }
    }

    serviceRef.current?.setLedEmissiveIntensity(
      dimmed ? NIGHT_LED_EMISSIVE_INTENSITY : DAY_LED_EMISSIVE_INTENSITY,
    );
    setSceneShadowsEnabled(!dimmed);
  }, [setSceneShadowsEnabled]);

  // Initialize Three.js scene - Matching Vue app rendering settings
  const initScene = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene with sky blue background (matching Vue app)
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(DEFAULT_SCENE_BG);
    sceneRef.current = scene;

    // Camera (FOV 50 to match Vue app)
    // Tightened near/far improves depth precision and reduces z-fighting in large scenes
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.5, 500);
    camera.position.set(-0.016, 1.057, 7.933);  // Vue app initial position
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer with Vue app settings
    // Enable logarithmic depth buffer to reduce z-fighting in large scenes
    const renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true, preserveDrawingBuffer: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);  // Full device pixel ratio for sharpness
    renderer.setClearColor(DEFAULT_SCENE_BG);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;  // Critical for correct colors
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls (matching Vue app)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.03;
    controls.minDistance = 3;
    controls.maxDistance = 30;
    controls.minPolarAngle = UNDERSIDE_VIEW_MIN_POLAR_ANGLE;
    controls.maxPolarAngle = UNDERSIDE_VIEW_MAX_POLAR_ANGLE;
    controls.target.set(0, 1.2, 0);
    controls.update();
    controlsRef.current = controls;

    // Skybox sphere (matching Vue app). Kept hidden — the configurator uses the
    // flat scene background (DEFAULT_SCENE_BG) instead of the sky.jpg image.
    // `null` is passed to setSkyboxAndGround so the scene-switcher never makes
    // it visible again; flip `SHOW_SKYBOX` to bring the sky back.
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(`${IMAGE_BASE_PATH}sky.jpg`, (texture) => {
      const skyMaterial = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide });
      const skyGeometry = new THREE.SphereGeometry(264, 264, 264);
      const skybox = new THREE.Mesh(skyGeometry, skyMaterial);
      skybox.visible = SHOW_SKYBOX;
      scene.add(skybox);
      skyboxRef.current = skybox;  // Store reference for scene switching

      // If service is already initialized, update skybox reference. When the
      // skybox is hidden we register `null` so prestigeService never toggles it.
      if (serviceRef.current && groundRef.current) {
        serviceRef.current.setSkyboxAndGround(
          SHOW_SKYBOX ? skybox : null,
          groundRef.current,
          baseRef.current ?? undefined,
        );
      }

      applyEnvironmentDimming(nightModeRef.current);
    });

    // Hemisphere light (Vue app has this)
    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x000000, 2);
    hemisphereLight.position.set(0, 0.1, 0);
    scene.add(hemisphereLight);
    hemisphereLightRef.current = hemisphereLight;

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambientLight);
    ambientLightRef.current = ambientLight;

    // Main directional light with shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(-2, 11, -5);
    directionalLight.target.position.set(0, 0, -1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 20;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    scene.add(directionalLight);
    keyLightRef.current = directionalLight;

    // Fill light from opposite side
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(2, 11, 5);
    directionalLight2.target.position.set(0, 0, -1);
    scene.add(directionalLight2);
    fillLightRef.current = directionalLight2;

    // Front and back lights
    const dirLightZz = new THREE.DirectionalLight(0xffffff, 0.55);
    dirLightZz.position.set(0, 0, -10);
    scene.add(dirLightZz);
    frontLightRef.current = dirLightZz;

    const dirLightZZ = new THREE.DirectionalLight(0xffffff, 0.95);
    dirLightZZ.position.set(0, 0, 10);
    scene.add(dirLightZZ);
    backLightRef.current = dirLightZZ;

    // Ground plane
    const groundGeometry = new THREE.PlaneGeometry(50, 50);
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      roughness: 0.9,
      metalness: 0.0,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    // TODO: Keep ground for shadows?
    scene.add(ground); // Disabled for testing
    groundRef.current = ground;  // Store reference for scene switching

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      syncDefaultFloorVisibilityForCamera({ camera });
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!container || !camera || !renderer) return;
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, [applyEnvironmentDimming, syncDefaultFloorVisibilityForCamera]);

  // Initialize Prestige service and load model
  const initPrestige = useCallback(async () => {
    if (!sceneRef.current) return;

    const service = createPrestigeService();
    serviceRef.current = service;

    service.init(sceneRef.current);

    try {
      await service.loadModel();

      // Create add icons on face surfaces
      await service.createAddIcons();

      // Create base and resize ground to match model (Vue parity)
      if (sceneRef.current) {
        const modelSize = service.getModelSize();
        const modelCenter = service.getModelCenter();

        if (groundRef.current) {
          groundRef.current.geometry.dispose();
          groundRef.current.geometry = new THREE.PlaneGeometry(modelSize.x * 5, modelSize.z * 5);
          groundRef.current.position.y = modelCenter.y - modelSize.y / 2;
        }

        if (!baseRef.current) {
          const baseGeometry = new THREE.BoxGeometry(modelSize.x * 5, 2, modelSize.z * 5);
          const baseMaterial = new THREE.MeshBasicMaterial({ color: 0xdddddd });
          const base = new THREE.Mesh(baseGeometry, baseMaterial);
          base.position.set(0, modelCenter.y - modelSize.y / 2 - 1.5 - 0.01, 0);
          base.receiveShadow = true;
          // TODO: Keep base for shadows?
          sceneRef.current.add(base); // Disabled for testing
          baseRef.current = base;
        } else {
          baseRef.current.geometry.dispose();
          baseRef.current.geometry = new THREE.BoxGeometry(modelSize.x * 5, 2, modelSize.z * 5);
          baseRef.current.position.set(0, modelCenter.y - modelSize.y / 2 - 1.5 - 0.01, 0);
        }
      }

      // Apply default frame and louver textures (Vue defaults)
      service.applyFrameColor('7015.png');
      service.applyBlindColor('7015.png');
      service.setLedEmissiveIntensity(
        nightModeRef.current ? NIGHT_LED_EMISSIVE_INTENSITY : DAY_LED_EMISSIVE_INTENSITY,
      );

      // Pass skybox and ground references for scene switching.
      // Ground is created synchronously, skybox may still be loading (handled in
      // its callback). When the skybox is hidden we register `null` for it so the
      // scene-switcher leaves it alone, while ground/base still toggle correctly.
      if (groundRef.current) {
        service.setSkyboxAndGround(
          SHOW_SKYBOX ? skyboxRef.current : null,
          groundRef.current,
          baseRef.current ?? undefined,
        );
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        loadProgress: 100,
        widthValue: service.getWidthValue(),
        depthValue: service.getDepthValue(),
        heightValue: service.getHeightValue(),
        roomCount: service.getRoomCount(),
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load model',
      }));
      console.error('Failed to load model:', error);
    }
  }, []);

  // Setup on mount
  useEffect(() => {
    const cleanup = initScene();
    initPrestige();

    return () => {
      cleanup?.();
      serviceRef.current?.dispose();
    };
  }, [initScene, initPrestige]);

  useEffect(() => {
    nightModeRef.current = state.nightMode;
    applyEnvironmentDimming(state.nightMode);
  }, [applyEnvironmentDimming, state.currentScene, state.nightMode]);

  // Setup click handling for face selection
  useEffect(() => {
    if (state.isLoading || !containerRef.current || !cameraRef.current || !serviceRef.current) return;

    raycasterRef.current = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleClick = (event: MouseEvent) => {
      const container = containerRef.current;
      const camera = cameraRef.current;
      const service = serviceRef.current;
      const raycaster = raycasterRef.current;

      if (!container || !camera || !service || !raycaster) return;

      // Only handle clicks on the canvas
      if (!(event.target instanceof HTMLCanvasElement)) return;

      // Calculate normalized mouse coordinates
      const rect = container.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update raycaster
      raycaster.setFromCamera(mouse, camera);

      // Check for remove icon clicks (always active) — removes installed accessory
      const removeIcons = service.getRemoveIcons().filter(icon => icon.visible);
      const removeIntersects = raycaster.intersectObjects(removeIcons, false);
      if (removeIntersects.length > 0) {
        const clickedIcon = removeIntersects[0].object;
        const faceId = (clickedIcon.userData as Record<string, unknown>).faceId as FaceIDType | undefined;
        if (faceId) {
          deletePartRef.current?.(faceId);
          return;
        }
      }

      // Then check for add icon clicks (only in add mode)
      if (addModeRef.current && activeAccessoryTypeRef.current !== null) {
        const addIcons = service.getAddIcons();
        const iconIntersects = raycaster.intersectObjects(addIcons, false);

        if (iconIntersects.length > 0) {
          const clickedIcon = iconIntersects[0].object;
          const userData = clickedIcon.userData as Record<string, unknown>;
          const faceId = userData.faceId as FaceIDType | undefined;

          if (faceId && service.canAddPart(faceId)) {
            void addPartRef.current?.(faceId, activeAccessoryTypeRef.current);
            return;
          }
        }
      }

      // Fall back to surface clicks
      const clickableSurfaces = service.getClickableSurfaces();
      const surfaceIntersects = raycaster.intersectObjects(clickableSurfaces, false);

      if (surfaceIntersects.length > 0) {
        const clickedObject = surfaceIntersects[0].object;
        const userData = clickedObject.userData as Record<string, unknown>;
        const faceId = userData.faceId as FaceIDType | undefined;

        if (faceId) {
          if (addModeRef.current && activeAccessoryTypeRef.current !== null) {
            if (service.canAddPart(faceId)) {
              void addPartRef.current?.(faceId, activeAccessoryTypeRef.current);
            }
          } else {
            // Not in add mode: clicking a face with an installed accessory
            // re-opens its customization slider (color / open-close controls).
            const surfacePart = service
              .getSurfacePartList()
              .find((part) => part.faceId === faceId);
            if (surfacePart?.partType !== null && surfacePart?.partType !== undefined) {
              openCustomizationRef.current?.(faceId, surfacePart.partType);
            }
          }
        }
      }
    };

    const container = containerRef.current;
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [state.isLoading]);

  // Actions
  const setWidth = useCallback(async (value: number) => {
    if (!serviceRef.current) return;
    const allowed = PRESTIGE_DIMENSIONS.width;
    const rounded = Math.round(value);
    let nextValue = rounded;
    if (!allowed.includes(rounded)) {
      let nearest = allowed[0];
      let minDiff = Math.abs(rounded - nearest);
      for (let i = 1; i < allowed.length; i += 1) {
        const candidate = allowed[i];
        const diff = Math.abs(rounded - candidate);
        if (diff < minDiff || (diff === minDiff && candidate < nearest)) {
          nearest = candidate;
          minDiff = diff;
        }
      }
      nextValue = nearest;
    }
    serviceRef.current.setWidth(nextValue);
    // Recreate add icons when room count changes
    await serviceRef.current.createAddIcons();
    setState(prev => ({
      ...prev,
      widthValue: nextValue,
      roomCount: serviceRef.current?.getRoomCount() ?? prev.roomCount,
    }));
  }, []);

  const setDepth = useCallback((value: number) => {
    if (!serviceRef.current) return;
    serviceRef.current.setDepth(value);
    void serviceRef.current.createAddIcons();
    setState(prev => ({ ...prev, depthValue: value }));
  }, []);

  const setHeight = useCallback((value: number) => {
    if (!serviceRef.current) return;
    serviceRef.current.setHeight(value);
    void serviceRef.current.createAddIcons();
    setState(prev => ({ ...prev, heightValue: value }));
  }, []);

  const setBlindRotation = useCallback((degrees: number) => {
    if (!serviceRef.current) return;
    serviceRef.current.setBlindRotation(degrees);
    setState(prev => ({ ...prev, blindRotation: degrees }));
  }, []);

  const applyFrameColor = useCallback((hex: string) => {
    serviceRef.current?.applyFrameColor(hex);
  }, []);

  const applyBlindColor = useCallback((hex: string) => {
    serviceRef.current?.applyBlindColor(hex);
  }, []);

  const setWallMounted = useCallback((value: boolean) => {
    if (!serviceRef.current) return;
    serviceRef.current.setWallMounted(value);
    // Update add icon visibility (North faces hidden when wall mounted)
    serviceRef.current.updateAddIconVisibility();
    setState(prev => ({ ...prev, hasWall: value }));
  }, []);

  const setUnit = useCallback((unit: UnitType) => {
    setState(prev => ({ ...prev, unit }));
  }, []);

  const canAddPart = useCallback((faceId: string): boolean => {
    return serviceRef.current?.canAddPart(faceId as FaceIDType) ?? false;
  }, []);

  const addPart = useCallback(async (faceId: string, partType: number, options?: AddPartOptions): Promise<void> => {
    if (!serviceRef.current) return;
    await serviceRef.current.addPart(faceId as FaceIDType, partType);
    // Update add icon visibility (hide icon for face that now has accessory)
    serviceRef.current.updateAddIconVisibility();
    if (options?.openCustomization === false) {
      return;
    }

    // Open customization modal for the new accessory
    const capabilities = serviceRef.current.getAccessoryCapabilities(partType);
    serviceRef.current.setAddIconsVisible(false);
    setState(prev => ({
      ...prev,
      isAddMode: false,
      activeAccessoryType: null,
      customizingFaceId: faceId as FaceIDType,
      customizingPartType: partType,
      customizingCapabilities: capabilities,
    }));
  }, []);

  const startAddMode = useCallback((partType: number) => {
    if (!serviceRef.current) return;
    serviceRef.current.setAddIconsVisible(true);
    void serviceRef.current.createAddIcons();
    setState(prev => ({
      ...prev,
      activeAccessoryType: partType,
      isAddMode: true,
    }));
  }, []);

  const stopAddMode = useCallback(() => {
    serviceRef.current?.setAddIconsVisible(false);
    setState(prev => ({
      ...prev,
      isAddMode: false,
      activeAccessoryType: null,
    }));
  }, []);

  const deletePart = useCallback((faceId: string) => {
    if (!serviceRef.current) return;
    serviceRef.current.deletePart(faceId as FaceIDType);
    // Update add icon visibility (show icon for face that no longer has accessory)
    serviceRef.current.updateAddIconVisibility();
    setState(prev => ({
      ...prev,
      customizingFaceId: prev.customizingFaceId === faceId ? null : prev.customizingFaceId,
      customizingPartType: prev.customizingFaceId === faceId ? null : prev.customizingPartType,
      customizingCapabilities: prev.customizingFaceId === faceId ? null : prev.customizingCapabilities,
    }));
  }, []);

  const deleteAllParts = useCallback(() => {
    if (!serviceRef.current) return;
    serviceRef.current.deleteAllParts();
    serviceRef.current.updateAddIconVisibility();
    setState(prev => ({
      ...prev,
      customizingFaceId: null,
      customizingPartType: null,
      customizingCapabilities: null,
    }));
  }, []);

  const setScene = useCallback(async (sceneId: SceneId): Promise<void> => {
    if (!serviceRef.current) return;
    await serviceRef.current.setScene(sceneId);
    serviceRef.current.updateAddIconVisibility();
    setState(prev => ({
      ...prev,
      currentScene: (serviceRef.current?.getCurrentScene() ?? sceneId) as SceneId,
      hasWall: serviceRef.current?.getHasWall() ?? prev.hasWall,
    }));
  }, []);

  const setSceneVisibility = useCallback((visible: boolean) => {
    serviceRef.current?.setSceneVisibility(visible);
  }, []);

  const toggleLedVisibility = useCallback(() => {
    if (!serviceRef.current) return;
    serviceRef.current.toggleLedVisibility();
    setState(prev => ({ ...prev, ledsVisible: !prev.ledsVisible }));
  }, []);

  const setHeaterQuantity = useCallback((qty: number) => {
    setState(prev => ({ ...prev, heaterQuantity: Math.max(0, qty) }));
  }, []);

  const toggleNightMode = useCallback(() => {
    const next = !nightModeRef.current;
    nightModeRef.current = next;
    applyEnvironmentDimming(next);
    setState(prev => ({ ...prev, nightMode: next }));
  }, [applyEnvironmentDimming]);

  const clearClickedFace = useCallback(() => {
    setState(prev => ({ ...prev, clickedFaceId: null }));
  }, []);

  const setAccessoryMovement = useCallback((value: number) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.moveAccessory(state.customizingFaceId, value);
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const setAccessoryMovementSecondary = useCallback((value: number) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.moveAccessorySecondary(state.customizingFaceId, value);
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const setAccessoryLeftRight = useCallback((isLeft: boolean) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.setAccessoryLeftRight(state.customizingFaceId, isLeft);
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const openCustomization = useCallback((faceId: FaceIDType, partType: number) => {
    if (!serviceRef.current) return;
    const capabilities = serviceRef.current.getAccessoryCapabilities(partType);
    setState(prev => ({
      ...prev,
      customizingFaceId: faceId,
      customizingPartType: partType,
      customizingCapabilities: capabilities,
    }));
  }, []);

  const closeCustomizationModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      customizingFaceId: null,
      customizingPartType: null,
      customizingCapabilities: null,
    }));
  }, []);

  const toggleMergeDoor = useCallback((doorIndex: number, visible: boolean) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.updateMergeDoorsStatus(state.customizingFaceId, doorIndex, visible);
    const surfacePart = serviceRef.current.getSurfacePartList()
      .find(part => part.faceId === state.customizingFaceId);
    if (!surfacePart?.part) {
      setState(prev => ({
        ...prev,
        customizingFaceId: null,
        customizingPartType: null,
        customizingCapabilities: null,
      }));
      return;
    }
    // Force re-render by updating state
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const setAccessoryTexture = useCallback((textureImg: string) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.setAccessoryTexture(state.customizingFaceId, textureImg);
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const setAccessoryTextureSecondary = useCallback((textureImg: string) => {
    if (!serviceRef.current || !state.customizingFaceId) return;
    serviceRef.current.setAccessoryTextureSecondary(state.customizingFaceId, textureImg);
    setState(prev => ({ ...prev }));
  }, [state.customizingFaceId]);

  const getMergeDoors = useCallback((): boolean[] => {
    if (!serviceRef.current || !state.customizingFaceId) return [];
    return serviceRef.current.getMergeDoors(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getMergeDoorCount = useCallback((): number => {
    if (!serviceRef.current || !state.customizingFaceId) return 0;
    return serviceRef.current.getMergeDoorCount(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getAccessoryMovement = useCallback((): number => {
    if (!serviceRef.current || !state.customizingFaceId) return 0;
    return serviceRef.current.getAccessoryMovement(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getAccessorySecondaryMovement = useCallback((): number => {
    if (!serviceRef.current || !state.customizingFaceId) return 0;
    return serviceRef.current.getAccessorySecondaryMovement(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getAccessoryLeftRight = useCallback((): boolean => {
    if (!serviceRef.current || !state.customizingFaceId) return true;
    return serviceRef.current.getAccessoryLeftRight(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getAccessoryTexture = useCallback((): string => {
    if (!serviceRef.current || !state.customizingFaceId) return '';
    return serviceRef.current.getAccessoryTexture(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const getAccessoryTextureSecondary = useCallback((): string => {
    if (!serviceRef.current || !state.customizingFaceId) return '';
    return serviceRef.current.getAccessoryTextureSecondary(state.customizingFaceId);
  }, [state.customizingFaceId]);

  const reset = useCallback(() => {
    if (!serviceRef.current) return;
    serviceRef.current.setWidth(19);
    serviceRef.current.setDepth(PRESTIGE_DIMENSIONS.depth[0] ?? 3);
    serviceRef.current.setHeight(PRESTIGE_DIMENSIONS.height[0] ?? 2.6);
    serviceRef.current.setBlindRotation(0);
    serviceRef.current.setWallMounted(false);
    serviceRef.current.setScene(null);
    serviceRef.current.setAddIconsVisible(false);
    // Turn off LEDs if they were on
    if (serviceRef.current.getLedsVisible()) {
      serviceRef.current.toggleLedVisibility();
    }
    nightModeRef.current = false;
    applyEnvironmentDimming(false);
    void serviceRef.current.createAddIcons();
    setState(prev => ({
      ...prev,
      widthValue: 19,
      depthValue: PRESTIGE_DIMENSIONS.depth[0] ?? 3,
      heightValue: PRESTIGE_DIMENSIONS.height[0] ?? 2.6,
      blindRotation: 0,
      roomCount: 1,
      hasWall: false,
      unit: 'inches',
      currentScene: null,
      ledsVisible: false,
      nightMode: false,
      clickedFaceId: null,
      activeAccessoryType: null,
      isAddMode: false,
      customizingFaceId: null,
      customizingPartType: null,
      customizingCapabilities: null,
    }));
  }, [applyEnvironmentDimming]);

  useEffect(() => {
    addPartRef.current = addPart;
  }, [addPart]);

  useEffect(() => {
    stopAddModeRef.current = stopAddMode;
  }, [stopAddMode]);

  useEffect(() => {
    deletePartRef.current = deletePart;
  }, [deletePart]);

  useEffect(() => {
    openCustomizationRef.current = openCustomization;
  }, [openCustomization]);

  useEffect(() => {
    addModeRef.current = state.isAddMode;
    activeAccessoryTypeRef.current = state.activeAccessoryType;
  }, [state.isAddMode, state.activeAccessoryType]);

  return {
    containerRef,
    state,
    actions: {
      setWidth,
      setDepth,
      setHeight,
      setBlindRotation,
      applyFrameColor,
      applyBlindColor,
      setWallMounted,
      setUnit,
      setScene,
      setSceneVisibility,
      toggleLedVisibility,
      setHeaterQuantity,
      toggleNightMode,
      canAddPart,
      addPart,
      deletePart,
      deleteAllParts,
      clearClickedFace,
      setAccessoryMovement,
      setAccessoryMovementSecondary,
      setAccessoryLeftRight,
      openCustomization,
      closeCustomizationModal,
      startAddMode,
      stopAddMode,
      toggleMergeDoor,
      setAccessoryTexture,
      setAccessoryTextureSecondary,
      getMergeDoors,
      getMergeDoorCount,
      getAccessoryMovement,
      getAccessorySecondaryMovement,
      getAccessoryLeftRight,
      getAccessoryTexture,
      getAccessoryTextureSecondary,
      reset,
    },
    service: serviceRef.current,
    view: {
      getCamera: () => cameraRef.current,
      getScene: () => sceneRef.current,
      captureImage: (mimeType = 'image/png', quality?: number) => {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        if (!renderer || !scene || !camera) return null;
        renderer.render(scene, camera);
        return renderer.domElement.toDataURL(mimeType, quality);
      },
    },
  };
};

// Re-export types and constants for convenience
export { FaceID, PRESTIGE_DIMENSIONS, SCENES };
export type { FaceIDType, SceneId, AccessoryCapabilities };
